// import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';

import './LeafletMap.css';

const MAX_ZOOM = 19;
// A remounted layer is appended last in the tile pane, so the stacking is pinned
// explicitly to keep street names above the base imagery after a map type switch.
const BASE_LAYER_Z_INDEX = 1;
const LABEL_LAYER_Z_INDEX = 2;

function CustomTiles({ url, zIndex = BASE_LAYER_Z_INDEX }) {
	const attribution =
		'&copy; <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank">City of New York CC BY 4.0</a>';
	// react-leaflet v3's TileLayer only re-syncs opacity/zIndex on prop updates
	// (see @react-leaflet/core's updateGridLayer), never the url, so switching
	// between map types without unmounting left the old tiles on screen. Keying
	// on url forces React to remount the layer with the new tile source.
	return <TileLayer key={url} attribution={attribution} url={url} maxZoom={MAX_ZOOM} zIndex={zIndex} />;
}

function LeafletMap(props) {
	const { propertyData, mapType, zoningData } = props;
	const START_ZOOM = 18;
	const latitude = props?.latitude || propertyData?.lat;
	const longitude = props?.longitude || propertyData?.['long'];

	if (!latitude || !longitude) {
		return (
			<div className="leaflet-map-wrapper">
				<div className="map-error-message">
					Latitude and longitude data not available for this property
				</div>
			</div>
		);
	}

	const tileUrl_Google =
		'http://mt0.google.com/vt/lyrs=s&hl=en&x={x}&y={y}&z={z}';
	const tileUrl_StreetNames =
		'https://maps.nyc.gov/xyz/1.0.0/carto/label-lt/{z}/{x}/{y}.png8';
	const tileUrl_1950s =
		'https://maps.nyc.gov/xyz/1.0.0/photo/1951/{z}/{x}/{y}.png8';
	const tileUrl_1920s =
		'https://maps.nyc.gov/xyz/1.0.0/photo/1924/{z}/{x}/{y}.png8';
	const tileUrl_Sanborn =
		'https://mapwarper.net/mosaics/tile/1194/{z}/{x}/{y}.png';

	let tileLayer = null;

	if (mapType === '1920s') {
		tileLayer = <CustomTiles url={tileUrl_1920s} />;
	} else if (mapType === '1950s') {
		tileLayer = <CustomTiles url={tileUrl_1950s} />;
	} else if (mapType === 'sanborn') {
		tileLayer = <CustomTiles url={tileUrl_Sanborn} />;
	} else if (mapType === 'street-sidewalk') {
		tileLayer = <CustomTiles url={tileUrl_StreetNames} />;
	} else if (mapType === 'street-satellite') {
		tileLayer = <CustomTiles url={tileUrl_Google} />;
	} else {
		tileLayer = <CustomTiles url={tileUrl_Google} />;
	}

	let streetNameLayer = <CustomTiles url={tileUrl_StreetNames} zIndex={LABEL_LAYER_Z_INDEX} />;

	//key=latlong to force react to re-render the map on property change
	return (
		<div className="leaflet-map-wrapper" key={`${latitude}${longitude}`}>
			<MapContainer
				center={[latitude, longitude]}
				zoom={START_ZOOM}
				scrollWheelZoom={false}
			>
				{tileLayer}
				{streetNameLayer}
				<Marker position={[latitude, longitude]}>
					<Popup>{propertyData?.address}</Popup>
				</Marker>
			</MapContainer>
			{mapType === 'street-sidewalk' && zoningData?.sanborn && (
				<div className="sanborn-map-number">
					Sanborn Map #: {zoningData.sanborn}
				</div>
			)}
			{mapType === 'sanborn' && (propertyData?.sanborn_volume || propertyData?.sanborn_page) && (
				<div className="sanborn-map-info">
					{propertyData?.sanborn_volume && <div>Volume: {propertyData.sanborn_volume}</div>}
					{propertyData?.sanborn_page && <div>Page: {propertyData.sanborn_page}</div>}
				</div>
			)}
		</div>
	);
}

export default LeafletMap;
